import React, { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { DollarSign, ShoppingCart, Receipt, Package } from "lucide-react";
import api from "@/lib/api";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const RANGES = [7, 30, 90];

function Stat({ icon: Icon, label, value, testid, color }) {
  return (
    <div className="bg-white rounded-3xl soft-shadow p-5 flex items-center gap-4" data-testid={testid}>
      <span className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: color }}><Icon className="w-5 h-5 text-[#19324A]" /></span>
      <div><p className="text-xs font-semibold text-[#19324A]/55">{label}</p><p className="font-display font-bold text-2xl text-[#19324A]">{value}</p></div>
    </div>
  );
}

export default function SalesDashboard() {
  const [days, setDays] = useState(30);
  const [s, setS] = useState(null);
  useEffect(() => { api.get(`/admin/stats?days=${days}`).then(({ data }) => setS(data)).catch(() => setS(null)); }, [days]);
  if (!s) return <p className="text-sm text-[#19324A]/50 p-6" data-testid="sales-loading">Loading sales…</p>;
  const chart = s.daily.map((d) => ({ ...d, label: d.date.slice(5).replace("-", "/") }));
  return (
    <div data-testid="sales-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <p className="text-sm text-[#19324A]/60">Paid orders, Queensland time. {s.excluded_orders > 0 && <span data-testid="sales-excluded">{s.excluded_orders} refunded/cancelled order{s.excluded_orders > 1 ? "s" : ""} excluded.</span>}</p>
        <div className="flex gap-1 bg-white rounded-full p-1 soft-shadow" data-testid="sales-range">
          {RANGES.map((r) => <button key={r} onClick={() => setDays(r)} data-testid={`sales-range-${r}`} className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${days === r ? "bg-[#FFD93D] text-[#19324A]" : "text-[#19324A]/60 hover:bg-[#FFF9EE]"}`}>{r}d</button>)}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat icon={DollarSign} label="Revenue" value={money(s.revenue)} testid="sales-revenue" color="#7ED957" />
        <Stat icon={ShoppingCart} label="Orders" value={s.orders} testid="sales-orders" color="#4CC9F0" />
        <Stat icon={Receipt} label="Avg order value" value={money(s.aov)} testid="sales-aov" color="#FFD93D" />
        <Stat icon={Package} label="Items sold" value={s.items_sold} testid="sales-items" color="#FF6B9D" />
      </div>
      <div className="bg-white rounded-3xl soft-shadow p-5 mb-6" data-testid="sales-chart">
        <h3 className="font-display font-bold text-[#19324A] mb-3">Daily revenue</h3>
        <div style={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#19324A14" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#19324A99" }} interval={days > 30 ? 9 : days > 7 ? 4 : 0} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#19324A99" }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
              <Tooltip formatter={(v, n) => (n === "revenue" ? [money(v), "Revenue"] : [v, "Orders"])} labelFormatter={(l) => `Day ${l}`} contentStyle={{ borderRadius: 16, border: "none", boxShadow: "0 8px 24px rgba(25,50,74,.12)" }} />
              <Bar dataKey="revenue" fill="#FF6B9D" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="bg-white rounded-3xl soft-shadow p-5" data-testid="sales-top">
        <h3 className="font-display font-bold text-[#19324A] mb-3">Best-sellers</h3>
        {s.top_products.length === 0 && <p className="text-sm text-[#19324A]/50" data-testid="sales-top-empty">No sales in this period yet.</p>}
        <ol className="divide-y divide-[#19324A]/8">
          {s.top_products.map((p, i) => (
            <li key={p.product_id || p.name} className="flex items-center gap-3 py-3" data-testid={`sales-top-${i}`}>
              <span className="w-7 h-7 rounded-full bg-[#FFD93D] font-display font-bold text-sm flex items-center justify-center text-[#19324A]">{i + 1}</span>
              {p.image && <img src={p.image} alt="" className="w-10 h-10 rounded-xl object-cover bg-[#FFF9EE]" />}
              <span className="flex-1 font-semibold text-[#19324A] text-sm">{p.name}</span>
              <span className="text-xs text-[#19324A]/60">{p.qty} sold</span>
              <span className="font-display font-bold text-[#19324A] text-sm">{money(p.revenue)}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
